// src/components/AuthBar.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../auth/useAuth";
import {
  isAppKitConnected,
  getAppKitAddress,
  subscribeToAppKitProvider,
  subscribeToWalletRequestState,
  getWalletRequestState,
  resetWalletRequestLock,
} from "../wallet/walletClient";

/**
 * ---------------------------------------------------------------------
 * ChainForge — AuthBar (Single Pill + Dropdown)
 * ---------------------------------------------------------------------
 *
 * SaaS UX:
 * - One pill in the top header
 * - Signed out: Connect / Sign in
 * - Signed in: address pill + dropdown
 *
 * Production hardening:
 * - Uses centralized auth state from useAuthInternal()
 * - Prevents duplicate wallet requests
 * - Respects global auth cooldown and retry countdown
 * - Avoids local duplicate auth state ownership
 */

function shorten(addr) {
  if (!addr) return "";
  const s = String(addr);
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function isEvmAddress(v) {
  return typeof v === "string" && /^0x[a-fA-F0-9]{40}$/.test(v.trim());
}

export default function AuthBar() {
  const {
    isAuthed,
    authLoading,
    authError,
    login,
    logout,
    switchWallet,
    connectWithWalletConnectAndLogin,
    session,
    isAuthCoolingDown,
    authCooldownRemainingSeconds,
  } = useAuth();

  const [walletAddress, setWalletAddress] = useState("");
  const [walletReady, setWalletReady] = useState(false);
  const [walletError, setWalletError] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [open, setOpen] = useState(false);
  const [walletRequestState, setWalletRequestState] = useState(() =>
    getWalletRequestState(),
  );

  useEffect(() => {
    setWalletRequestState(getWalletRequestState());

    const unsubscribe = subscribeToWalletRequestState((state) => {
      setWalletRequestState({
        pending: Boolean(state?.pending),
        label: String(state?.label || ""),
      });
    });

    return () => {
      try {
        unsubscribe?.();
      } catch {
        // ignore cleanup errors
      }
    };
  }, []);

  useEffect(() => {
    const combinedError = String(walletError || authError || "").toLowerCase();

    if (
      combinedError.includes("cancelled") ||
      combinedError.includes("canceled") ||
      combinedError.includes("closed") ||
      combinedError.includes("failed")
    ) {
      setConnecting(false);
    }
  }, [walletError, authError]);

  const syncStoredWalletAddress = useCallback((addr) => {
    const normalized = String(addr || "").trim();

    try {
      if (normalized) {
        localStorage.setItem("chainforge_wallet_address", normalized);
      } else {
        localStorage.removeItem("chainforge_wallet_address");
      }

      window.dispatchEvent(new CustomEvent("chainforge:wallet-changed"));
    } catch {
      // ignore storage errors
    }
  }, []);

  const logoutInProgressRef = useRef(false);
  const walletRestoreSuppressedRef = useRef(false);

  const authedAddress = useMemo(() => {
    return isAuthed && session?.address ? String(session.address) : "";
  }, [isAuthed, session]);

  const hardLogout = useCallback(async () => {
    logoutInProgressRef.current = true;
    walletRestoreSuppressedRef.current = true;

    setOpen(false);
    setWalletAddress("");
    syncStoredWalletAddress("");
    setWalletError("");

    try {
      await logout();
    } catch {
      // ignore
    } finally {
      window.setTimeout(() => {
        logoutInProgressRef.current = false;
      }, 0);
    }
  }, [logout, syncStoredWalletAddress]);

  const effectiveAddress = useMemo(() => {
    return authedAddress || walletAddress || "";
  }, [authedAddress, walletAddress]);

  const walletRequestPending = Boolean(walletRequestState?.pending);

  const busy = Boolean(
    connecting ||
    authLoading ||
    isAuthCoolingDown ||
    walletRequestPending,
  );

  const pillLabel = useMemo(() => {
    if (walletRequestPending) {
      return "Check wallet…";
    }

    const displayAddress = effectiveAddress || walletAddress || "";

    if (!isEvmAddress(displayAddress)) {
      return connecting ? "Connecting…" : "Connect Wallet";
    }

    if (!isAuthed) {
      if (isAuthCoolingDown) {
        return `Wait ${authCooldownRemainingSeconds || 0}s`;
      }
      return authLoading ? "Signing…" : shorten(displayAddress);
    }

    return shorten(displayAddress);
  }, [
    walletRequestPending,
    walletAddress,
    connecting,
    isAuthed,
    authLoading,
    effectiveAddress,
    isAuthCoolingDown,
    authCooldownRemainingSeconds,
  ]);

  const readWallet = useCallback(async () => {
    setWalletError("");

    if (logoutInProgressRef.current || walletRestoreSuppressedRef.current) {
      setWalletAddress("");
      syncStoredWalletAddress("");
      setWalletReady(true);
      return;
    }

    try {
      const appKitAddress = String(getAppKitAddress() || "").trim();

      if (isAppKitConnected() && isEvmAddress(appKitAddress)) {
        setWalletAddress(appKitAddress);
        syncStoredWalletAddress(appKitAddress);
        setWalletReady(true);
        return;
      }

      setWalletAddress("");
      syncStoredWalletAddress("");
    } catch {
      setWalletAddress("");
      syncStoredWalletAddress("");
      setWalletError("Unable to read wallet.");
    } finally {
      setWalletReady(true);
    }
  }, [syncStoredWalletAddress]);

  useEffect(() => {
    readWallet();
  }, [readWallet]);


  useEffect(() => {
    const unsubscribe = subscribeToAppKitProvider(
      ({ address, isConnected, error }) => {
        if (
          error ||
          logoutInProgressRef.current ||
          walletRestoreSuppressedRef.current
        ) {
          return;
        }

        const addr = String(address || getAppKitAddress() || "").trim();
        const connected =
          typeof isConnected === "boolean"
            ? isConnected
            : isAppKitConnected();

        if (connected && isEvmAddress(addr)) {
          setWalletAddress(addr);
          syncStoredWalletAddress(addr);
        } else if (typeof isConnected === "boolean" && !isConnected) {
          setWalletAddress("");
          syncStoredWalletAddress("");
          setOpen(false);
        }
      },
    );

    return () => {
      try {
        unsubscribe?.();
      } catch {
        // ignore cleanup errors
      }
    };
  }, [syncStoredWalletAddress]);

  useEffect(() => {
    async function verifyLiveWallet() {
      if (
        logoutInProgressRef.current ||
        walletRestoreSuppressedRef.current ||
        connecting ||
        authLoading ||
        walletRequestPending
      ) {
        return;
      }

      const addr = String(getAppKitAddress() || "").trim();
      if (isAppKitConnected() && isEvmAddress(addr)) {
        return;
      }

      // Do not force logout just because AppKit temporarily reports no address.
      // AppKit can briefly clear address during signing, chain switching,
      // WalletConnect relay issues, page focus changes, or network hiccups.
      // JWT auth should only be cleared by explicit logout, wallet switch,
      // or failed backend session validation.
      if (!isAuthed && walletAddress) {
        setWalletAddress("");
        syncStoredWalletAddress("");
      }
    }

    const onFocus = () => {
      verifyLiveWallet();
    };

    const onVisibility = () => {
      if (!document.hidden) {
        verifyLiveWallet();
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    const intervalId = window.setInterval(() => {
      verifyLiveWallet();
    }, 1500);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(intervalId);
    };
  }, [
    walletAddress,
    isAuthed,
    hardLogout,
    connecting,
    authLoading,
    walletRequestPending,
    syncStoredWalletAddress,
  ]);


  const connectWallet = useCallback(async () => {
    walletRestoreSuppressedRef.current = false;
    setWalletError("");
    setConnecting(true);

    try {
      const result = await connectWithWalletConnectAndLogin();

      if (!result?.ok) {
        setWalletError(result?.error || "Wallet connection failed.");
        return "";
      }

      const addr = String(result?.address || "").trim();

      if (!isEvmAddress(addr)) {
        throw new Error("Wallet returned an invalid address.");
      }

      setWalletError("");
      setWalletAddress(addr);
      syncStoredWalletAddress(addr);

      return addr;
    } catch (err) {
      const rawMessage = String(err?.message || "").toLowerCase();
      const message =
        rawMessage.includes("cancelled") ||
          rawMessage.includes("canceled") ||
          rawMessage.includes("closed") ||
          rawMessage.includes("provider not found")
          ? "Wallet connection was cancelled."
          : err?.message || "Wallet connection failed.";

      setWalletError(message);
      return "";
    } finally {
      setConnecting(false);
    }
  }, [connectWithWalletConnectAndLogin, syncStoredWalletAddress]);

  const signInOnly = useCallback(async () => {
    setWalletError("");
    setConnecting(true);

    try {
      const result = await login();

      if (!result?.ok) {
        setWalletError(result?.error || "Sign in failed.");
        return "";
      }

      const addr = String(result?.address || walletAddress || "").trim();

      if (isEvmAddress(addr)) {
        setWalletError("");
        setWalletAddress(addr);
        syncStoredWalletAddress(addr);
      }

      return addr;
    } catch (err) {
      setWalletError(err?.message || "Sign in failed.");
      return "";
    } finally {
      setConnecting(false);
    }
  }, [login, walletAddress, syncStoredWalletAddress]);

  const onPillClick = useCallback(async () => {
    if (busy) return;

    const appKitAddress = String(getAppKitAddress() || "").trim();

    const hasConnectedWallet =
      isEvmAddress(walletAddress) ||
      (isAppKitConnected() && isEvmAddress(appKitAddress));

    // 1) No wallet yet -> connect + login
    if (!hasConnectedWallet && !isAuthed) {
      await connectWallet();
      return;
    }

    // 2) Wallet already connected, but auth missing -> sign only
    if (hasConnectedWallet && !isAuthed) {
      await signInOnly();
      return;
    }

    // 3) Authenticated -> open dropdown
    setOpen((v) => !v);
  }, [isAuthed, walletAddress, connectWallet, signInOnly, busy]);

  const onSwitchWallet = useCallback(async () => {
    setWalletError("");
    setWalletError("You switched wallets. Please sign in again to continue.");
    setOpen(false);
    setConnecting(true);

    try {
      const result = await switchWallet();

      if (!result?.ok) {
        setWalletError(result?.error || "Wallet switch failed.");
        return;
      }

      const addr = String(result?.address || "").trim();

      if (isEvmAddress(addr)) {
        setWalletError("");
        setWalletAddress(addr);
        syncStoredWalletAddress(addr);
      }
    } catch (err) {
      setWalletError(err?.message || "Wallet switch failed.");
    } finally {
      setConnecting(false);
    }
  }, [switchWallet, syncStoredWalletAddress]);

  // close dropdown on outside click
  const wrapRef = useRef(null);
  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!walletReady) {
    return <div className="text-xs text-slate-500">Loading…</div>;
  }

  return (
    <div className="relative" ref={wrapRef}>
      {/* Single pill */}
      <button
        onClick={onPillClick}
        disabled={busy}
        className="inline-flex items-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:border-slate-400 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        title={effectiveAddress || "Connect wallet"}
      >
        {pillLabel}
      </button>

      {/* Dropdown only when authenticated */}
      {isAuthed && open && (
        <div className="absolute right-0 mt-2 w-80 overflow-hidden rounded-xl border bg-white text-black shadow-lg">
          <div className="border-b p-3">
            <div className="text-xs text-gray-600">Workspace Session</div>
            <div className="text-sm font-mono">
              {authedAddress || effectiveAddress || "-"}
            </div>
          </div>

          <button
            onClick={onSwitchWallet}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
          >
            Switch Wallet
          </button>

          <button
            onClick={hardLogout}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
          >
            Sign out
          </button>
        </div>
      )}

      {/* Wallet pending message */}
      {walletRequestPending && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-amber-200 bg-white p-3 text-xs text-amber-800 shadow-lg">
          <div className="font-semibold">
            {walletRequestState?.label || "Wallet request pending"}
          </div>

          <div className="mt-1 leading-5">
            Open your wallet app or browser extension and approve or cancel the request. Do not click Connect again while it is pending.
          </div>

          <button
            type="button"
            onClick={() => {
              resetWalletRequestLock();
              setConnecting(false);
              setWalletError(
                "Wallet request state reset. If your wallet still shows a request, cancel it inside the wallet, then try again.",
              );
            }}
            className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900 hover:bg-amber-100"
          >
            Reset wallet state
          </button>
        </div>
      )}

      {/* Error message */}
      {!walletRequestPending && (walletError || authError) && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-red-200 bg-white p-3 text-xs text-red-700 shadow-lg">
          {walletError || authError}
        </div>
      )}
    </div>
  );
}
